// @flow

import React, {useState, useRef, useMemo} from 'react';
import CustomAvatar from '../../../../../../../../elements/CustomAvatar/CustomAvatar';
import {
  makeStyles,
  Grid,
  Typography,
  IconButton,
  Popover,
  Avatar,
} from '@material-ui/core';

import type {Theme} from '@material-ui/core';
import {colors} from '../../../../../../../../assets/styles/colors';
import ImagePreviewModal from '../../../../../../../../elements/ImagePreviewModal';
import FileMessage from './FileMessage';
import DocumentMessage from './DocumentMessage';

import type {
  ImageSource,
  ConversationMessage,
  ReactionMessage,
} from '../../../../../../../../entities';
import VideoMessage from './VideoMessage';
import AudioMessage from './AudioMessage';
import MapMessage from './MapMessage';
import MessageFormatter from '../../../../../../../../elements/TextMessage/message-formatter';
import ImageMessage from './ImageMessage';
import {formatTimestampTo12Hour} from '../../../../../../../../utils/date/time';
import ForwardIcon from '../../../../../../../../elements/icons/ForwardIcon';

import TiendadaTooltip from '../../../../../../../../elements/tiendada-tooltip';
import {InsertEmoticon, Add, ExpandMore, Reply} from '@material-ui/icons';
import EmojiPicker from '../../../../../../../../elements/EmojiPicker/EmojiPicker';
import CustomButton from '../../../../../../../../elements/CustomButton/CustomButton';
import {AvatarGroup} from '@material-ui/lab';
import ReplySnippet from './ReplySnippet';
import {classNames} from '../../../../../../../../utils/react';
import BotIcon from '../../../../../../../../elements/icons/BotIcon';

type Props = {
  message: ConversationMessage,

  reactions?: ReactionMessage[],
  highlighted?: boolean,

  onForward: (message: ConversationMessage) => void,
  onReact: (message: ConversationMessage, emoji: string) => void,
  onClickRepliedMessage: (token: string, messageId: string) => void,
  onReply?: (message: ConversationMessage) => void,
  onRemoveReaction: (message: ConversationMessage, reactionId: string) => void,
};

const REACTION_OPTIONS = [
  {
    emoji: '👍',
    name: 'Like',
  },
  {
    emoji: '❤️',
    name: 'Love',
  },
  {
    emoji: '😂',
    name: 'Laugh',
  },
  {
    emoji: '😮',
    name: 'Wow',
  },
  {
    emoji: '😢',
    name: 'Sad',
  },
  {
    emoji: '😡',
    name: 'Angry',
  },
];

const REACTION_SUPPORTED_SERVICES = ['WHATSAPP_WEB'];
const REPLY_SUPPORTED_SERVICES = ['WHATSAPP_WEB', 'FB_MESSENGER', 'INSTAGRAM'];

const useStyles = props =>
  makeStyles((theme: Theme) => ({
    message: {
      width: '100%',
      paddingLeft: props.self ? '.75rem' : 0,
      paddingRight: !props.self ? '.75rem' : 0,
      position: 'relative',

      '&:hover $forwardButton': {
        opacity: 1,
        transform: 'translateY(-50%) scale(1)',
      },
    },

    content: {
      width: '90%',
      display: 'flex',
      alignItems: 'flex-end',
      gap: '.5rem',

      '&[data-self="true"]': {
        flexDirection: 'row-reverse',
      },

      [theme.breakpoints.up('sm')]: {
        width: '85%',
      },

      [theme.breakpoints.up('md')]: {
        width: '80%',
      },
    },
    messageRow: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.25rem',
      '&[data-self="true"]': {
        alignItems: 'flex-end',
      },
    },
    messageWrap: {
      width: 'fit-content',
      position: 'relative',
      borderRadius: `8px 8px ${props.self ? '0px 8px' : '8px 0px'}`,
      background: props.self ? '#d5bfff' : 'white',
      padding: '.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '.25rem',

      '&:hover $moreOptionsButton': {
        opacity: 1,
      },
    },
    highlightedWrap: {
      background: '#7b39ff2e',
      borderRadius: '.5rem',
    },
    forwardButton: {
      top: '50%',
      transform: 'translateY(-50%) scale(0)',
      left: props.self ? '-2.25rem' : 'auto',
      right: props.self ? 'auto' : '-2.25rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      opacity: 0,
      transition: 'opacity .2s ease-in-out',
    },
    textMessageWrap: {
      padding: '.25rem',
      width: 'fit-content',
      maxWidth: '100%',
      alignSelf: 'flex-start',
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: 14,

      '&[data-self="true"]': {
        alignSelf: 'flex-end',
      },
    },
    text: {
      color: props.self ? colors.grey900 : colors.grey800,
    },
    pictureMessageWrap: {
      gap: '.5rem',
      background: props.self ? '#d5bfff' : '#F4F4F6',
      borderRadius: '.75rem',
      padding: '.5rem',
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      maxWidth: '100%',
      height: 'auto',
      maxHeight: '300px',
      position: 'relative',

      '&[data-single="true"]': {
        gridTemplateColumns: '1fr',
        aspectRatio: '1/1',
      },

      '&:hover .see-more-picture-button': {
        opacity: 1,
      },
      '&:focus-within .see-more-picture-button': {
        opacity: 1,
      },
    },
    pictureMessage: {
      width: '100%',
      height: '80px',
      borderRadius: '.75rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      overflow: 'hidden',

      '&[data-single="true"]': {
        height: 'auto !important',
      },

      [theme.breakpoints.up('xl')]: {
        height: '120px',
      },

      '&:nth-child(1)': {
        gridColumn: '1 / 3',
      },

      '&:nth-child(2)': {
        gridColumn: '3 / 4',
      },

      '&:nth-child(3)': {
        gridColumn: '1 / 2',
      },

      '&:nth-child(4)': {
        gridColumn: '2 / 4',
      },

      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform .2s ease-in-out',
        borderRadius: 'inherit',
      },

      '&:hover img': {
        transform: 'scale(1.1)',
      },
    },
    seeMorePictureButton: {
      position: 'absolute',
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      background: `${colors.grey200}80`,
      backdropFilter: 'blur(1rem)',
      color: colors.white,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      border: 'none',
      opacity: 0,
      fontWeight: 500,
      transition: 'opacity .2s ease-in-out',

      top: '50%',
      transform: 'translateY(-50%)',
      right: '-2.25rem',

      '&[data-self="true"]': {
        left: '-2.25rem',
        right: 'auto',
      },
    },
    fileMessageWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.5rem',
      maxWidth: '100%',
    },
    sentAt: {
      whiteSpace: 'nowrap',
    },
    moreOptionsButton: {
      position: 'absolute',
      top: 0,
      left: props.self ? '0' : 'initial',
      right: props.self ? 'initial' : '0',
      opacity: 0,
      background: 'rgba(255, 255, 255, 0.5)',
      backdropFilter: 'blur(1rem)',
    },
    popoverPaper: {
      padding: '.5rem',
      borderRadius: '2rem',
      backdropFilter: 'blur(1rem)',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    popoverContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.25rem',
      padding: '.5rem',
      borderRadius: '.75rem',
      backdropFilter: 'blur(1rem)',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    reactionButtonContainer: {
      display: 'flex',
    },
    reactionButton: {
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'initial',
      '&[data-selected="true"]': {
        background: colors.grey400,
      },
    },
    emojisWrap: {
      position: 'absolute',
    },
    messageReactionContainer: {
      position: 'absolute',
      bottom: '-.5rem',
    },
    messageReaction: {
      width: '1.5rem',
      height: '1.5rem',
      minWidth: '1.5rem',
      minHeight: '1.5rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: colors.grey200,
      color: 'initial',
      borderRadius: '50%',
      fontSize: '1rem',
    },
  }));

function Message(props: Props) {
  const {
    message,
    highlighted,

    onReply,
    onForward,
    onReact,
    onRemoveReaction,
    onClickRepliedMessage,
  } = props;
  const {
    text,
    fromMe: self,
    images,
    documents,
    videos,
    audios,
    service,
    isUnreachable,
    metadata,
  } = message;

  const classes = useStyles({self})();

  const modalOpenRef = useRef(false);
  const [selectedImage, setSelectedImage] = useState<null | ImageSource>(null);
  const [anchorPopoverEl, setAnchorPopoverEl] = useState(null);

  const [anchorEmojiEl, setAnchorEmojiEl] = useState(null);
  const emojiPopoverOpen = Boolean(anchorEmojiEl);

  const [moreOptionsEl, setMoreOptionsEl] = useState(null);
  const moreOptionsOpen = Boolean(moreOptionsEl);

  const reactionsProps = props.reactions || [];
  const reactions = useMemo(() => {
    return reactionsProps.filter(i => i.messageId === message.id) ?? [];
  }, [message.id, reactionsProps]);

  const handleImageClick = (src: string | Blob) => {
    setSelectedImage(src);
    modalOpenRef.current = true;
  };

  const handleCloseModal = () => {
    modalOpenRef.current = false;
    setSelectedImage(null);
  };

  const handlePopoverOpen = event => {
    setAnchorPopoverEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorPopoverEl(null);
  };

  const handleEmojiPopoverOpen = event => {
    setAnchorEmojiEl(event.currentTarget);
  };
  const handleEmojiPopoverClose = () => {
    setAnchorEmojiEl(null);
  };

  const onEmojiPick = emoji => {
    handleSelectReaction(emoji.native || emoji.id);
  };

  const handleMoreOptionsOpen = event => {
    setMoreOptionsEl(event.currentTarget);
  };

  const handleMoreOptionsClose = () => {
    setMoreOptionsEl(null);
  };

  const handleSelectReaction = (emoji: string) => {
    onReact(message, emoji);
    handlePopoverClose();
    handleEmojiPopoverClose();
  };

  const handleRemoveReaction = (reactionId: string) => {
    onRemoveReaction(message, reactionId);
    handlePopoverClose();
    handleEmojiPopoverClose();
  };

  const openPopover = Boolean(anchorPopoverEl);

  const myReaction = reactions.find(r => r.fromMe && r.reaction);
  const myReactionIsInDefaults = REACTION_OPTIONS.some(
    option => option.emoji === myReaction?.reaction
  );

  const canReact = REACTION_SUPPORTED_SERVICES.includes(service);
  const canReply = REPLY_SUPPORTED_SERVICES.includes(service);

  const getSenderAvatarData = () => {
    if (!metadata) {
      return {
        name: self ? 'Tú' : 'Cliente',
        initials: self ? 'T' : 'C',
      };
    }
    if (metadata.source === 'CHATBOT') {
      return {
        name: 'Bot',
        initials: 'B',
      };
    }

    const lastname = metadata.user.lastname || '';
    const firstname = metadata.user.firstname || '';

    let fullName = `${firstname} ${lastname}`.trim();

    if (metadata.user.type === 'VENDOR') {
      fullName = `Vendedor ${fullName}`;
    }

    if (metadata.user.type === 'COLLABORATOR') {
      fullName = `Colaborador ${fullName}`;
    }

    return {
      name: fullName,
      initials: `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase(),
    };
  };

  const {name: senderName, initials: avatarContent} = getSenderAvatarData();

  return (
    <div className={classes.message}>
      {selectedImage && images && (
        <ImagePreviewModal
          open={modalOpenRef.current}
          onClose={handleCloseModal}
          images={images.filter(img => img.src).map(img => img.src || '')}
          defaultImage={selectedImage}
        />
      )}

      <Grid
        container
        direction={self ? 'row-reverse' : 'row'}
        alignItems="flex-end"
        spacing={1}
      >
        <Grid
          item
          xs={1}
          style={{
            paddingBottom: '1.5rem',
          }}
        >
          {!metadata && <CustomAvatar />}
          {metadata && (
            <TiendadaTooltip title={senderName} placement="top">
              <div>
                <CustomAvatar>
                  {metadata.source === 'CHATBOT' ? <BotIcon /> : avatarContent}
                </CustomAvatar>
              </div>
            </TiendadaTooltip>
          )}
        </Grid>
        <Grid
          item
          xs={10}
          lg={9}
          className={classNames({
            [classes.highlightedWrap]: highlighted,
          })}
        >
          <div className={classes.messageRow} data-self={self}>
            <div className={classes.messageWrap}>
              {message.repliedMessage && (
                <ReplySnippet
                  message={message.repliedMessage}
                  onClick={() => {
                    if (!message.repliedMessage) return;
                    onClickRepliedMessage(
                      message.repliedMessage.messageReceivedAt,
                      message.repliedMessage.messageId
                    );
                  }}
                />
              )}
              {images && (
                <div
                  className={classes.pictureMessageWrap}
                  data-single={images.length === 1}
                >
                  {images
                    .filter(img => img.src || img.mediaId)
                    .slice(0, 4)
                    .map((image, index) => (
                      <ImageMessage
                        single={images.length === 1}
                        key={index}
                        image={image}
                        self={self}
                        onClick={src => handleImageClick(src)}
                      />
                    ))}

                  {images.length > 4 && (
                    <button
                      className={`${classes.seeMorePictureButton} see-more-picture-button`}
                      data-self={self}
                      onClick={() => {
                        setSelectedImage(images[4].src || '');
                      }}
                    >
                      {images.length - 4}+
                    </button>
                  )}
                </div>
              )}
              {documents && (
                <div className={classes.fileMessageWrap}>
                  {documents.map((file, index) => {
                    // Document with full data
                    if (file.src) {
                      return (
                        <FileMessage
                          key={index}
                          src={file.src}
                          name={file.name}
                          self={self}
                          size={file.size}
                          isUnreachable={isUnreachable}
                          type={file.mimetype}
                        />
                      );
                    }
                    // Document with only mediaId - fetch via WhatsApp API
                    if (file.mediaId) {
                      return (
                        <DocumentMessage
                          key={index}
                          document={file}
                          self={self}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              )}
              {videos &&
                videos.map((video, index) => (
                  <VideoMessage key={index} self={self} video={video} />
                ))}
              {audios &&
                audios.map((audio, index) => (
                  <AudioMessage key={index} self={self} audio={audio} />
                ))}
              {message.coords && (
                <MapMessage coords={message.coords} self={self} />
              )}
              {text && (
                <div className={classes.textMessageWrap} data-self={self}>
                  <MessageFormatter
                    message={text}
                    chatServiceType={message.service}
                  />
                </div>
              )}
              {/*               <TiendadaTooltip title="Reenviar mensaje" placement="top">
                <IconButton
                  className={classes.forwardButton}
                  onClick={onForward}
                  size="small"
                >
                  <ForwardIcon />
                </IconButton>
              </TiendadaTooltip> */}
              {canReact && (
                <TiendadaTooltip title="Reaccionar" placement="top">
                  <IconButton
                    className={classes.forwardButton}
                    onClick={handlePopoverOpen}
                    size="small"
                  >
                    <InsertEmoticon />
                  </IconButton>
                </TiendadaTooltip>
              )}
              <Popover
                open={openPopover}
                anchorEl={anchorPopoverEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                classes={{
                  paper: classes.popoverPaper,
                }}
              >
                <div className={classes.reactionButtonContainer}>
                  {REACTION_OPTIONS.map((option, index) => (
                    <TiendadaTooltip
                      key={index}
                      title={option.name}
                      placement="top"
                    >
                      <IconButton
                        onClick={() => {
                          if (
                            myReaction &&
                            myReaction.reaction === option.emoji
                          ) {
                            handleRemoveReaction(myReaction.id);
                          } else {
                            handleSelectReaction(option.emoji);
                          }
                        }}
                        className={classes.reactionButton}
                        data-selected={myReaction?.reaction === option.emoji}
                        size="small"
                      >
                        <span role="img" aria-label={option.name}>
                          {option.emoji}
                        </span>
                      </IconButton>
                    </TiendadaTooltip>
                  ))}
                  {!myReactionIsInDefaults && myReaction && (
                    <TiendadaTooltip placement="top">
                      <IconButton
                        onClick={() => {
                          handleRemoveReaction(myReaction.id);
                        }}
                        className={classes.reactionButton}
                        data-selected
                        size="small"
                      >
                        <span role="img" aria-label={myReaction.reaction}>
                          {myReaction.reaction}
                        </span>
                      </IconButton>
                    </TiendadaTooltip>
                  )}
                  <TiendadaTooltip placement="top" title="Más">
                    <IconButton
                      onClick={handleEmojiPopoverOpen}
                      className={classes.reactionButton}
                      size="small"
                    >
                      <Add />
                    </IconButton>
                  </TiendadaTooltip>
                </div>
              </Popover>
              <Popover
                open={emojiPopoverOpen}
                anchorEl={anchorEmojiEl}
                onClose={() => {
                  handleEmojiPopoverClose();
                }}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                classes={{
                  paper: classes.popoverPaper,
                }}
              >
                <EmojiPicker
                  onEmojiPick={onEmojiPick}
                  previewPosition="none"
                  onClickOutside={handleEmojiPopoverClose}
                />
              </Popover>
              <TiendadaTooltip title="Más opciones" placement="top">
                <IconButton
                  className={classes.moreOptionsButton}
                  onClick={handleMoreOptionsOpen}
                  size="small"
                >
                  <ExpandMore />
                </IconButton>
              </TiendadaTooltip>
              <Popover
                open={moreOptionsOpen}
                anchorEl={moreOptionsEl}
                onClose={handleMoreOptionsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: self ? 'left' : 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: self ? 'left' : 'right',
                }}
                classes={{
                  paper: classes.popoverContent,
                }}
              >
                <CustomButton
                  endIcon={<ForwardIcon />}
                  onClick={() => {
                    handleMoreOptionsClose();
                    onForward(message);
                  }}
                >
                  Reenviar
                </CustomButton>
                {canReply && (
                  <CustomButton
                    endIcon={<Reply />}
                    onClick={() => {
                      handleMoreOptionsClose();
                      if (onReply) {
                        onReply(message);
                      }
                    }}
                  >
                    Responder
                  </CustomButton>
                )}
              </Popover>
              {reactions.length > 0 && (
                <div className={classes.messageReactionContainer}>
                  <AvatarGroup>
                    {reactions.map(reaction => (
                      <Avatar
                        alt={reaction.reaction}
                        className={classes.messageReaction}
                      >
                        <span role="img" aria-label="heart">
                          {reaction?.reaction ?? ''}
                        </span>
                      </Avatar>
                    ))}
                  </AvatarGroup>
                </div>
              )}
            </div>
            <Typography variant="caption" className={classes.sentAt}>
              {formatTimestampTo12Hour(message.receivedAt)}
            </Typography>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

// $FlowFixMe
const MemoizedMessage = React.memo(Message);

export default MemoizedMessage;
